import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { BOOKINGS_FOR_USER_STUDIOS } from '../graphql/queries/bookingQueries';
import { getCloudFrontImageUrl } from '../util/cloudFrontUtils';
import awsConfig from '../config/aws-config';
import { buildCoarseLocationText } from '../util/locationPrivacy';

export const useFetchBookedStudios = (userID) => {
  const { data, loading, error, refetch } = useQuery(BOOKINGS_FOR_USER_STUDIOS, {
    variables: { userID },
    skip: !userID,
    fetchPolicy: 'cache-and-network',
  });

  const processedStudio = useMemo(() => {
    if (loading) return { items: [], nextToken: null };
    if (error || !data?.studiosByUserID?.items) return { items: [], nextToken: null };
    
    // Process all bookings from all studios
    const processedBookings = data.studiosByUserID.items.flatMap((studio) => {
      // Skip studios without bookings
      if (!studio.bookings?.items || !Array.isArray(studio.bookings.items)) {
        return [];
      }

      // Process studio images
      const processedImages = studio.images?.map(imageKey => {
        if (!imageKey) return '';
        try {
          return getCloudFrontImageUrl(imageKey, { 
            width: 1200,
            height: 800, 
            fit: 'cover' 
          });
        } catch (error) {
          try {
            const cleanKey = imageKey.startsWith('public/') ? imageKey : `public/${imageKey}`;
            return `https://${awsConfig.Storage.S3.bucket}.s3.${awsConfig.Storage.S3.region}.amazonaws.com/${cleanKey}`;
          } catch (fallbackError) {
            return '';
          }
        }
      }) || [];

      // Map each booking in this studio
      return studio.bookings.items.map((booking) => {
        if (!booking) return null;

        // Process user profile picture
        let processedProfilePicture = '';
        if (booking.User?.profilePicture) {
          try {
            processedProfilePicture = getCloudFrontImageUrl(booking.User.profilePicture, {
              width: 200,
              height: 200,
              fit: 'cover'
            });
          } catch (error) {
            processedProfilePicture = '';
          }
        }

        return {
          ...booking,
          studioID: studio.id,
          Studio: {
            id: studio.id,
            StudioName: studio.StudioName,
            images: studio.images,
            processedImages,
            studioLocation: buildCoarseLocationText(studio),
            city: studio.city,
            country: studio.country,
            locationLabel: studio.locationLabel
          },
          User: {
            ...booking.User,
            processedProfilePicture
          }
        };
      }).filter(Boolean); // Remove any null entries
    });

    return {
      items: processedBookings,
      nextToken: data.studiosByUserID.nextToken || null
    };
  }, [data, loading, error]);

  return {
    bookingStudioData: processedStudio,
    loading,
    error,
    refetch
  };
};
